import { Link } from "react-router-dom";
import FaqSchema from "@/components/FaqSchema";
import ResourceLayout, { AnswerBlock, DataTable } from "@/components/ResourceLayout";

const FAQS = [
  {
    q: "What is a recipe formula in professional cooking?",
    a: "A recipe formula is a scaled ratio between the ingredients that make a dish, expressed either as absolute weights or as percentages of a base ingredient. Bakers call this the baker's percentage. Chefs use ratios like a 3:2:1 pie dough or a 5:1 mother sauce to keep a plate consistent as batch size changes.",
  },
  {
    q: "What is baker's percentage and how do I use it?",
    a: "Baker's percentage sets the flour weight to one hundred percent and expresses every other ingredient as a percentage of that flour. Water at 65 percent, salt at 2 percent, yeast at 1 percent. It lets you scale a bread recipe from one loaf to fifty without redoing the math each time.",
  },
  {
    q: "How do I convert a recipe from home portions to production quantities?",
    a: "Divide the target yield by the source yield to get a conversion factor, then multiply every ingredient by that factor. Recheck seasoning, cooking time, and pan size at production scale. A recipe that serves four scaled to forty will not simply take ten times longer to cook.",
  },
];

export default function RecipeFormulas() {
  return (
    <ResourceLayout
      activeSlug="recipe-formulas"
      title="Recipe Formulas: the ratios that keep a kitchen consistent"
      lede="Reference tables for baker's percentage, classical mother sauce ratios, brine ratios, dough hydration, and pastry cream. The formulas every station memorises so a dish tastes the same on the tenth Friday as it did on the first."
    >
      <FaqSchema
        id="recipe-formulas"
        title="Recipe Formulas and Ratios for Professional Kitchens | Culinaire Specialist"
        description="Baker's percentage, mother sauce ratios, dough hydration, brine strength, and pastry cream formulas for consistent plating at any batch size."
        canonicalUrl="https://www.culinairespecialist.com/resources/recipe-formulas"
        faqs={FAQS}
      />

      <AnswerBlock
        id="what-is-a-recipe-formula"
        heading="What is a recipe formula and why do professional kitchens use one?"
        answer="A recipe formula is a ratio between ingredients rather than a fixed weight list. It lets a kitchen scale a dish up or down without rewriting the recipe. The most common examples are baker's percentage in bread and pastry, and the classical mother sauce ratios that underpin European cuisine."
        softwareNote={<>Culinaire Specialist stores every dish as both an absolute quantity and a ratio. Scale a recipe from six covers to sixty in one input, and every ingredient scales in lockstep. <Link to="/register" className="gold underline">Start free</Link>.</>}
      />

      <AnswerBlock
        id="bakers-percentage"
        heading="How to use baker's percentage on the pass"
        answer="Set the flour weight at one hundred percent and express every other ingredient as a percentage of that flour. Water at sixty five percent means six hundred and fifty grams of water for every one thousand grams of flour. It is the fastest way to scale bread, brioche, and enriched doughs."
      >
        <DataTable
          caption="Baker's percentage examples by dough style"
          columns={["Dough", "Flour", "Water", "Salt", "Yeast"]}
          rows={[
            ["Standard bread", "100 %", "65 %", "2 %", "1 %"],
            ["Ciabatta (high hydration)", "100 %", "80 to 85 %", "2 %", "0.6 %"],
            ["Baguette", "100 %", "67 to 72 %", "2 %", "0.8 %"],
            ["Pizza Napoletana", "100 %", "60 to 65 %", "2.5 %", "0.15 %"],
            ["Focaccia", "100 %", "80 to 90 %", "2 %", "1 %"],
            ["Brioche", "100 %", "40 %", "2 %", "2 %"],
          ]}
          testId="recipe-formulas-table-bakers"
        />
      </AnswerBlock>

      <AnswerBlock
        id="mother-sauce-ratios"
        heading="Classical mother sauce ratios every station should know"
        answer="French classical cuisine rests on five mother sauces, each with a defining ratio that keeps texture predictable. Beurre blanc, hollandaise, and pan sauces work from a similar principle: a fat, a liquid, and a stabiliser in fixed proportion. Ratios below produce a nappe consistency that coats the back of a spoon."
      >
        <DataTable
          caption="Mother sauce and finishing sauce ratios"
          columns={["Sauce", "Ratio (by weight)", "Notes"]}
          rows={[
            ["Béchamel", "1 : 1 : 20 (butter : flour : milk)", "Cook roux blond before adding milk"],
            ["Velouté", "1 : 1 : 20 (butter : flour : stock)", "Chicken, veal, or fish stock"],
            ["Espagnole", "1 : 1 : 20 (fat : flour : brown stock)", "Roux cooked to brun"],
            ["Tomate", "1 : 4 (roux : tomato liquid)", "Modern versions skip roux entirely"],
            ["Hollandaise", "3 yolks : 250 g butter : 15 ml acid", "Warm, not hot; break above 65 °C"],
            ["Beurre blanc", "1 : 4 (reduction : cold butter)", "Whisk cold butter, never boil"],
            ["Mayonnaise", "1 yolk : 250 ml oil : 5 ml acid", "Drip oil in slowly"],
          ]}
          testId="recipe-formulas-table-sauces"
        />
      </AnswerBlock>

      <AnswerBlock
        id="brines-and-cures"
        heading="Brine, cure, and pickle ratios"
        answer="A brine seasons and hydrates protein by holding water and salt in equilibrium. A cure removes water with salt and sugar. Both are ratio driven. A five percent wet brine is right for poultry and pork; a two percent quick pickle brine holds vegetables crisp for a week in the walk-in."
      >
        <DataTable
          caption="Brine, cure, and pickle ratios"
          columns={["Application", "Salt", "Sugar", "Water or vinegar"]}
          rows={[
            ["Wet brine, poultry", "5 %", "2 %", "100 % water"],
            ["Wet brine, pork", "5 to 6 %", "3 %", "100 % water"],
            ["Turkey overnight brine", "4 to 5 %", "3 %", "100 % water"],
            ["Dry cure, salmon gravlax", "3 % of fish weight", "3 % of fish weight", "n/a"],
            ["Bacon cure", "2.5 % of pork weight", "1 % of pork weight", "n/a, plus 0.25 % pink salt"],
            ["Quick pickle brine", "2 %", "6 to 8 %", "50 : 50 vinegar : water"],
            ["Classic dill pickle", "3.5 %", "0 %", "100 % 5 % vinegar"],
          ]}
          testId="recipe-formulas-table-brine"
        />
      </AnswerBlock>

      <AnswerBlock
        id="pastry-basics"
        heading="Pastry cream, ganache, and pie dough ratios"
        answer="Pastry lives on ratios more than any other section. A 3:2:1 pie dough is three parts flour, two parts fat, one part water by weight. A one-to-one dark ganache is one hundred grams of dark chocolate per one hundred grams of hot cream. Once memorised, these ratios never need re-checking."
      >
        <DataTable
          caption="Common pastry ratios"
          columns={["Preparation", "Ratio (by weight)", "Notes"]}
          rows={[
            ["Shortcrust pie dough", "3 : 2 : 1 (flour : fat : water)", "Keep butter under 10 °C"],
            ["Puff pastry", "1 : 1 (dough : butter block)", "3 single and 1 double turn"],
            ["Dark ganache", "1 : 1 (chocolate : cream)", "Whisk from centre outward"],
            ["Milk chocolate ganache", "2 : 1 (chocolate : cream)", "Higher chocolate for firmer set"],
            ["Pastry cream", "1 L milk : 6 yolks : 200 g sugar : 80 g starch", "Boil starch fully, one minute"],
            ["Choux paste", "1 : 1 : 2 : 2 (water : butter : flour : eggs)", "By weight, add eggs one at a time"],
            ["Meringue Italian", "2 : 1 (sugar : egg white)", "Sugar syrup at 118 °C into whipping whites"],
          ]}
          testId="recipe-formulas-table-pastry"
        />
      </AnswerBlock>
    </ResourceLayout>
  );
}
